import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Search } from 'lucide-react';
import { AdminLayout } from './AdminLayout';
import { SupportConversation } from '../support/SupportConversation';
import { useSupportDraftGuard } from '../support/useSupportDraftGuard';
import { useSupportIdentity, useSupportRefresh } from '../../../stores/appSupportStore';
import { SUPPORT_TYPES, supportService, SupportEmailLog, SupportStatus, SupportThread, SupportType } from '../../../services/support/supportService';
import { getAllAdminUsers, AdminUserWithStaff } from '../../../services/admin/adminService';
import { cn } from '../../../lib/utils';
import { cardBg, borderGrey, primaryText, secondaryText } from '../../../styling/stylingHelper';

const controlClass = 'rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 px-3 py-2 min-h-[44px]';

function SupportEmailHistory({ threadId, revision }: { threadId: string; revision: number }) {
  const { t, i18n } = useTranslation('support');
  const [logs, setLogs] = useState<SupportEmailLog[]>([]);
  const [failed, setFailed] = useState(false);
  const request = useRef<AbortController | null>(null);
  useEffect(() => () => { request.current?.abort(); }, []);
  const refresh = async () => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = await supportService.emailLogs(threadId, controller.signal);
      if (controller.signal.aborted) return;
      setLogs(response.logs);
      setFailed(false);
    } catch (error) { if (!controller.signal.aborted) { setFailed(true); throw error; } }
  };
  useSupportRefresh(refresh, 60_000, `${threadId}:${revision}`);
  return <section className="mt-5 border-t border-gray-200 dark:border-gray-700 pt-4">
    <h3 className={cn('font-semibold mb-2', primaryText)}>{t('admin.emailTitle')}</h3>
    {failed ? <button className={cn(controlClass, 'text-sm')} onClick={() => { void refresh().catch(() => {}); }}>{t('admin.retry')}</button> :
      logs.length === 0 ? <p className={cn('text-sm', secondaryText)}>{t('admin.emailUnknown')}</p> :
      <ul className="space-y-2 max-h-52 overflow-y-auto">
        {logs.map(log => <li key={log.id} className={cn('text-sm break-words', secondaryText)}>
          <time dateTime={log.sentAt}>{new Date(log.sentAt).toLocaleString(i18n.language)}</time>{' · '}{log.recipientEmail}{' · '}
          {t(log.status === 'failed' ? 'admin.emailFailed' : log.bouncedAt ? 'admin.emailBounced' : log.openedAt ? 'admin.emailOpened' : log.deliveredAt ? 'admin.emailDelivered' : 'admin.emailSent')}
        </li>)}
      </ul>}
  </section>;
}

function Inbox() {
  const { t, i18n } = useTranslation('support');
  const identity = useSupportIdentity();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('thread');
  const [status, setStatus] = useState<SupportStatus | ''>('awaiting_support');
  const [type, setType] = useState<SupportType | ''>('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [account, setAccount] = useState('');
  const [accounts, setAccounts] = useState<AdminUserWithStaff[]>([]);
  const [accountsError, setAccountsError] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [data, setData] = useState<{ scope: string; threads: SupportThread[]; cursor: string | null } | null>(null);
  const [error, setError] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [revision, setRevision] = useState(0);
  const [deletionNotice, setDeletionNotice] = useState<string | null>(null);
  const dirty = useRef(false);
  useSupportDraftGuard(dirty, t('admin.discardDraft'));
  const request = useRef<AbortController | null>(null);
  const scope = JSON.stringify([identity, status, type, query, account, unreadOnly]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const threads = data?.scope === scope ? data.threads : [];

  useEffect(() => { const timer = setTimeout(() => setQuery(search.trim()), 250); return () => clearTimeout(timer); }, [search]);
  const loadAccounts = useCallback(async () => {
    try { const result = await getAllAdminUsers(); setAccounts(result.adminUsers); setAccountsError(false); }
    catch { setAccountsError(true); }
  }, []);
  useEffect(() => { void loadAccounts(); }, [loadAccounts]);
  useEffect(() => () => { request.current?.abort(); }, []);

  const refresh = useCallback(async (more = false) => {
    if (!identity) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    if (more) setLoadingMore(true);
    try {
      const response = await supportService.listThreads({
        status: status || undefined, type: type || undefined, search: query || undefined,
        accountOwnerId: account || undefined, unreadOnly: unreadOnly || undefined,
        cursor: more && data?.scope === scope ? data.cursor ?? undefined : undefined,
      }, true, controller.signal);
      if (controller.signal.aborted || currentScope.current !== scope) return;
      setData(previous => ({ scope, threads: more && previous?.scope === scope
        ? Array.from(new Map([...previous.threads, ...response.threads].map(thread => [thread.id, thread])).values())
        : response.threads, cursor: response.nextCursor }));
      setError(false);
    } catch (failure) {
      if (!controller.signal.aborted && currentScope.current === scope) { setError(true); throw failure; }
    } finally { if (currentScope.current === scope) setLoadingMore(false); }
  }, [identity, status, type, query, account, unreadOnly, data, scope]);
  useSupportRefresh(() => refresh(), 60_000, identity ? scope : null);

  const chooseThread = (id: string | null) => {
    if (id === selectedId) return;
    if (dirty.current && !window.confirm(t('admin.discardDraft'))) return;
    dirty.current = false;
    const next = new URLSearchParams(params);
    if (id) next.set('thread', id); else next.delete('thread');
    setParams(next);
  };
  const onChanged = useCallback(() => { setRevision(value => value + 1); void refresh().catch(() => {}); }, [refresh]);
  const onDeleted = (attachmentCleanupPending: boolean) => {
    request.current?.abort();
    dirty.current = false;
    setData(previous => previous ? { ...previous, threads: previous.threads.filter(thread => thread.id !== selectedId) } : previous);
    chooseThread(null);
    setDeletionNotice(attachmentCleanupPending ? 'admin.deletedCleanupPending' : 'admin.deleted');
    void refresh().catch(() => {});
  };

  return <AdminLayout>
    <div className="max-w-7xl mx-auto p-4 lg:p-6">
      <h1 className={cn('text-2xl font-semibold', primaryText)}>{t('admin.title')}</h1>
      <p className={cn('mt-1 mb-5', secondaryText)}>{t('admin.subtitle')}</p>
      {deletionNotice && <p role="status" className={cn('mb-4 text-sm', secondaryText)}>{t(deletionNotice)}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <label className={cn(controlClass, 'flex items-center gap-2')}>
          <Search className="w-4 h-4 shrink-0" aria-hidden="true" />
          <input aria-label={t('admin.search')} placeholder={t('admin.search')} value={search} onChange={event => setSearch(event.target.value)} className="min-w-0 w-full bg-transparent dark:bg-transparent outline-none" />
        </label>
        <select className={controlClass} aria-label={t('admin.allStatuses')} value={status} onChange={event => setStatus(event.target.value as SupportStatus | '')}>
          <option value="">{t('admin.allStatuses')}</option>
          {(['awaiting_support', 'awaiting_customer', 'resolved'] as const).map(value => <option key={value} value={value}>{t(`statuses.${value}`)}</option>)}
        </select>
        <select className={controlClass} aria-label={t('admin.allTypes')} value={type} onChange={event => setType(event.target.value as SupportType | '')}>
          <option value="">{t('admin.allTypes')}</option>
          {SUPPORT_TYPES.map(value => <option key={value} value={value}>{t(`types.${value}`)}</option>)}
        </select>
        <select className={controlClass} aria-label={t('admin.account')} value={account} onChange={event => setAccount(event.target.value)}>
          <option value="">{t('admin.allAccounts')}</option>
          {accounts.map(value => <option key={value.id} value={value.id}>{value.name || value.email}</option>)}
        </select>
      </div>
      {accountsError && <button className={cn(controlClass, 'mb-3')} onClick={() => { void loadAccounts(); }}>{t('admin.retry')}</button>}
      <label className={cn('flex gap-2 items-center mb-4 min-h-[44px]', secondaryText)}>
        <input type="checkbox" checked={unreadOnly} onChange={event => setUnreadOnly(event.target.checked)} />{t('admin.unreadOnly')}
      </label>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,0.85fr)_minmax(0,1.5fr)] items-start">
        <section className={cn('border rounded-2xl p-3 min-w-0', cardBg, borderGrey, selectedId && 'hidden lg:block')}>
          {error && <button className={cn(controlClass, 'mb-3 w-full')} onClick={() => { void refresh().catch(() => {}); }}>{t('admin.retry')}</button>}
          {!data || data.scope !== scope ? !error && <p role="status" className={secondaryText}>{t('admin.loading')}</p> : threads.length === 0 ? <p className={cn('p-3', secondaryText)}>{t('admin.empty')}</p> :
            <ul className="space-y-2">{threads.map(thread => <li key={thread.id}>
              <button onClick={() => chooseThread(thread.id)} className={cn('w-full text-left p-3 rounded-xl border break-words', borderGrey,
                thread.id === selectedId ? 'bg-emerald-50 dark:bg-emerald-950' : 'hover:bg-gray-50 dark:hover:bg-gray-800', primaryText)}>
                <span className="flex items-start gap-2"><span className="font-semibold flex-1">{thread.subject}</span>
                  {thread.unread && <span aria-label={t('unreadLabel', { count: 1 })} className="mt-1.5 h-2 w-2 rounded-full bg-emerald-600 dark:bg-emerald-400 shrink-0" />}
                </span>
                <span className={cn('block text-xs mt-1', secondaryText)}>{thread.accountName} · {thread.creatorName}</span>
                {thread.restaurantName && <span className={cn('block text-xs', secondaryText)}>{thread.restaurantName}</span>}
                <span className={cn('block text-sm mt-2 line-clamp-2', secondaryText)}>{thread.lastMessagePreview}</span>
                <span className={cn('block text-xs mt-2', secondaryText)}>{t(`types.${thread.type}`)} · {t(`statuses.${thread.status}`)}</span>
                <time className={cn('block text-xs mt-1', secondaryText)} dateTime={thread.lastMessageAt}>{new Date(thread.lastMessageAt).toLocaleString(i18n.language)}</time>
              </button>
            </li>)}</ul>}
          {data?.scope === scope && data.cursor && <button disabled={loadingMore} onClick={() => { void refresh(true).catch(() => {}); }} className={cn(controlClass, 'mt-3 w-full disabled:opacity-50')}>{t('admin.more')}</button>}
        </section>
        <section className={cn('border rounded-2xl p-4 min-w-0', cardBg, borderGrey, !selectedId && 'hidden lg:block')}>
          {selectedId ? <>
            <button onClick={() => chooseThread(null)} className={cn('lg:hidden flex items-center gap-2 mb-4 min-h-[44px]', primaryText)}><ArrowLeft className="w-4 h-4" />{t('admin.title')}</button>
            <div className="h-[70vh] min-h-[400px]"><SupportConversation threadId={selectedId} admin onChanged={onChanged} onDeleted={onDeleted} onDraftChange={value => { dirty.current = value; }} /></div>
            <SupportEmailHistory key={selectedId} threadId={selectedId} revision={revision} />
          </> : <p className={secondaryText}>{t('admin.selectThread')}</p>}
        </section>
      </div>
    </div>
  </AdminLayout>;
}

export default function AdminSupportPage() {
  const identity = useSupportIdentity();
  return identity ? <Inbox key={identity} /> : null;
}
